// What a switched-off helper withholds from a view (RULES_SCHEMA §15c,
// PLAN-2026-10-29-helper-tools-switch §2).
//
// ONE COPY, which the league's stores call on each view they serve, once it is
// built, and the web app's reads of the practice league's simulator call on
// each of its views (`apps/web/src/data/hooks.ts`). It never reaches into how a
// view was made, so the bots, pick'em's bots and the draft's autopick, which
// read projections through their own paths, keep them: a switch is about what
// the site shows a manager, never how the bots play.
//
// Pure, and each function returns the view unchanged when nothing it carries is
// switched off, so a league with every helper on pays a comparison a view.

import type {
  ClaimView,
  DraftView,
  DraftPickRef,
  DropOption,
  FieldRowView,
  HomeView,
  HubView,
  LineupRowView,
  LineupView,
  MatchupRowView,
  MatchupSummary,
  MatchupView,
  PlayerRowView,
  PlayersPage,
  RosterMoveView,
  ScheduleView,
  ScoreboardView,
  TradeBuilderView,
  TradeRosterRow,
  TradesView,
  TradeView,
  WaiverPlayerRow,
  WaiversView,
} from '../api/views';
import type { PlayerCardView } from '../api/player-card';
import type { CompareView } from '../api/compare';
import type { WorthALookView } from '../api/worth';
import type { SuggestionsView } from '../api/suggestions';
import type { ToolsRef } from './tools';

const all = (t: ToolsRef) => t.projections && t.suggestions && t.tradeAnalysis && t.sandbox;

// ── Projections ────────────────────────────────────────────────────────────

/** A row's projected points and where they came from. */
function unprojected<T extends { projection: number | null; basis?: unknown }>(row: T): T {
  return 'basis' in row ? { ...row, projection: null, basis: null } : { ...row, projection: null };
}

/**
 * A matchup's projected side and its win chance, until it is final: then the
 * one is the score and the other the result, which are not forecasts.
 */
function unforecast<
  T extends Pick<MatchupSummary, 'status' | 'home' | 'away' | 'winProbabilityHome'>,
>(s: T): T {
  if (s.status === 'final') return s;
  return {
    ...s,
    home: { ...s.home, projected: null },
    away: { ...s.away, projected: null },
    winProbabilityHome: null,
  };
}

export function withholdLineup(view: LineupView, t: ToolsRef): LineupView {
  if (t.projections && t.sandbox) return view;
  const rows = (list: LineupRowView[]) => (t.projections ? list : list.map(unprojected));
  return {
    ...view,
    starters: rows(view.starters),
    bench: rows(view.bench),
    reserve: rows(view.reserve),
    projectedTotal: t.sandbox ? view.projectedTotal : null,
  };
}

export function withholdSummary(s: MatchupSummary, t: ToolsRef): MatchupSummary {
  return t.projections ? s : unforecast(s);
}

export function withholdMatchup(view: MatchupView, t: ToolsRef): MatchupView {
  if (t.projections) return view;
  const side = (s: MatchupView['home']): MatchupView['home'] => ({
    ...s,
    rows: s.rows.map((r: MatchupRowView) => unprojected(r)),
    bench: s.bench.map((r: MatchupRowView) => unprojected(r)),
  });
  const v = unforecast(view);
  return { ...v, home: side(v.home), away: side(v.away) };
}

function withholdField(r: FieldRowView, t: ToolsRef): FieldRowView {
  return t.projections || r.status === 'final' ? r : { ...r, projected: null };
}

export function withholdScoreboard(view: ScoreboardView, t: ToolsRef): ScoreboardView {
  if (t.projections) return view;
  return {
    ...view,
    matchups: view.matchups.map(unforecast),
    field: view.field === null ? null : view.field.map((r) => withholdField(r, t)),
  };
}

/** Home's matchup and field row, by its league's switches. */
export function withholdHome(view: HomeView): HomeView {
  return withholdGlance(view);
}

/** The hub: each league's card by that league's own switches. */
export function withholdHub(view: HubView): HubView {
  return { ...view, leagues: view.leagues.map(withholdGlance) };
}

function withholdGlance<V extends Pick<HomeView, 'league' | 'matchup' | 'secondMatchup' | 'field'>>(
  view: V,
): V {
  const t = view.league.tools;
  if (t.projections) return view;
  return {
    ...view,
    matchup: view.matchup === null ? null : unforecast(view.matchup),
    secondMatchup: view.secondMatchup === null ? null : unforecast(view.secondMatchup),
    field: view.field === null ? null : withholdField(view.field, t),
  };
}

/** The season's weeks: each week's matchups, mine among them. */
export function withholdSchedule(view: ScheduleView): ScheduleView {
  const t = view.league.tools;
  if (t.projections) return view;
  return {
    ...view,
    periods: view.periods.map((p) => ({
      ...p,
      myMatchup: p.myMatchup === null ? null : unforecast(p.myMatchup),
      mySecond: p.mySecond === null ? null : unforecast(p.mySecond),
      myField: p.myField === null ? null : withholdField(p.myField, t),
      matchups: p.matchups.map(unforecast),
    })),
  };
}

// ── Players, the card, Compare, Waivers, the move sheet ────────────────────

export function withholdPlayerRow(row: PlayerRowView, t: ToolsRef): PlayerRowView {
  if (t.projections && t.suggestions) return row;
  return {
    ...row,
    ...(t.projections ? {} : { projection: null, basis: null, posRank: null }),
    ...(t.suggestions ? {} : { trend: null, adds: null }),
  };
}

export function withholdPlayersPage(page: PlayersPage, t: ToolsRef): PlayersPage {
  if (t.projections && t.suggestions) return page;
  return { ...page, items: page.items.map((r) => withholdPlayerRow(r, t)) };
}

export function withholdCard(card: PlayerCardView, t: ToolsRef): PlayerCardView {
  if (t.projections && t.suggestions) return card;
  return {
    ...card,
    ...(t.projections ? {} : { projection: null, basis: null }),
    ...(t.suggestions ? {} : { trend: null, adds: null }),
  };
}

export function withholdCompare(view: CompareView, t: ToolsRef): CompareView {
  if (t.projections) return view;
  return {
    ...view,
    players: view.players.map((p) => ({ ...p, projection: null, basis: null })),
    highest: { ...view.highest, projection: [] },
  };
}

export function withholdWaivers(view: WaiversView, t: ToolsRef): WaiversView {
  if (t.projections) return view;
  return {
    ...view,
    myClaims: view.myClaims.map((c: ClaimView) => ({ ...c, projection: null })),
    onWaivers: view.onWaivers.map((r: WaiverPlayerRow) => unprojected(r)),
  };
}

export function withholdMove(view: RosterMoveView, t: ToolsRef): RosterMoveView {
  if (t.projections) return view;
  return {
    ...view,
    projection: null,
    roster: view.roster.map((o: DropOption) => ({ ...o, projection: null })),
  };
}

// ── Trades ─────────────────────────────────────────────────────────────────

function tradeRow(r: TradeRosterRow, t: ToolsRef): TradeRosterRow {
  return {
    ...r,
    projection: t.projections ? r.projection : null,
    worth: t.tradeAnalysis ? r.worth : null,
  };
}

const pick = (p: DraftPickRef, t: ToolsRef): DraftPickRef =>
  t.tradeAnalysis ? p : { ...p, worth: null };

export function withholdTrade(view: TradeView, t: ToolsRef): TradeView {
  if (t.projections && t.tradeAnalysis) return view;
  const side = (s: TradeView['sides'][number]) => ({
    ...s,
    players: s.players.map((r) => tradeRow(r, t)),
    draftPicks: s.draftPicks.map((p) => pick(p, t)),
  });
  return {
    ...view,
    sides: [side(view.sides[0]), side(view.sides[1])],
    readout: t.tradeAnalysis ? view.readout : null,
  };
}

export function withholdTradeBuilder(view: TradeBuilderView, t: ToolsRef): TradeBuilderView {
  if (t.projections && t.tradeAnalysis) return view;
  return {
    ...view,
    mine: view.mine.map((r) => tradeRow(r, t)),
    theirs: view.theirs.map((r) => tradeRow(r, t)),
    myPicks: view.myPicks.map((p) => pick(p, t)),
    theirPicks: view.theirPicks.map((p) => pick(p, t)),
    myDropOptions: t.projections
      ? view.myDropOptions
      : view.myDropOptions.map((o) => ({ ...o, projection: null })),
    readout: t.tradeAnalysis ? view.readout : null,
  };
}

export function withholdTrades(view: TradesView): TradesView {
  const t = view.league.tools;
  if (all(t)) return view;
  const trades = (list: TradeView[]) => list.map((x) => withholdTrade(x, t));
  return {
    ...view,
    forMe: trades(view.forMe),
    sent: trades(view.sent),
    review: trades(view.review),
    history: trades(view.history),
    block: t.projections ? view.block : view.block.map((b) => ({ ...b, projection: null })),
    picks: view.picks.map((p) => pick(p, t)),
    depth: view.depth === null || t.suggestions ? view.depth : { ...view.depth, partners: [] },
  };
}

// ── The draft ────────────────────────────────────────────────────────────

/**
 * The board's projections: a real league's are null already (its board ranks
 * by last season, `draft-reads.ts`); the practice league's simulator fills
 * them.
 */
export function withholdDraft(view: DraftView): DraftView {
  if (view.league.tools.projections) return view;
  return { ...view, available: view.available.map((r) => ({ ...r, projection: null })) };
}

// ── Suggestions ────────────────────────────────────────────────────────────

export function withholdWorth(view: WorthALookView, t: ToolsRef): WorthALookView {
  if (!t.suggestions) return { ...view, positions: [], nextUp: [], off: true };
  if (t.projections) return view;
  const item = <I extends { row: PlayerRowView }>(i: I): I => ({
    ...i,
    row: withholdPlayerRow(i.row, t),
  });
  return {
    ...view,
    positions: view.positions.map((p) => ({ ...p, items: p.items.map(item) })),
    nextUp: view.nextUp.map(item),
  };
}

/**
 * Off with suggestions, and with projections too: this read picks and orders
 * its players by our projection (`kind: 'top'`), which nothing may line up by
 * while projections are off.
 */
export function withholdSuggestions(view: SuggestionsView, t: ToolsRef): SuggestionsView {
  if (!t.suggestions || !t.projections) return { ...view, holes: [], items: [], off: true };
  return view;
}
